// WebSocket Alert Types for Admin Portal
// Based on ADMIN_PORTAL_WEBSOCKET_API_DOCUMENTATION.md

export type AlertPriority = 'critical' | 'high' | 'normal' | 'low';

export type AlertCategory = 'orders' | 'payments' | 'breaches' | 'stock' | 'status' | 'general';

export interface AlertMeta {
    timestamp: string;    // ISO 8601 format
    priority: AlertPriority;
    eventType: string;
}

export interface BaseAlert {
    _meta: AlertMeta;
}

// Event: new_order
export interface NewOrderAlert extends BaseAlert {
    orderId: string;
    orderNumber: string;
    status: string;
    totalAmount: number;
    itemCount: number;
    customerName: string;
    createdAt: string;
}

// Event: order_status_change
export interface OrderStatusChangeAlert extends BaseAlert {
    orderId: string;
    orderNumber: string;
    previousStatus: string;
    newStatus: string;
    totalAmount: number;
    customerName: string;
    updatedBy: {
        userId: string;
        userType: string;
        userName: string;
    } | null;
}

// Event: assignment_alert
export interface AssignmentAlert extends BaseAlert {
    orderId: string;
    orderNumber: string;
    assignmentType: 'supplier' | 'transporter';
    assigneeId: string;
    assigneeName: string;
    orderStatus: string;
    totalAmount: number;
}

// Event: payment_update
export interface PaymentUpdateAlert extends BaseAlert {
    paymentId: string;
    orderId: string;
    orderNumber: string;
    status: 'success' | 'failed' | 'pending' | 'refunded';
    amount: number;
    method: string;
    customerName: string;
}

// Event: breach_alert
export interface BreachAlert extends BaseAlert {
    breachId: string;
    orderId: string;
    orderNumber: string;
    breachType: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    responsibleParty: 'supplier' | 'transporter' | 'admin' | 'customer';
    status: string;
    description: string;
    impact: Record<string, unknown>;
    detectedAt: string;
}

// Event: breach_escalation
export interface BreachEscalationAlert extends BaseAlert {
    breachId: string;
    orderId: string;
    orderNumber: string;
    breachType: string;
    severity: string;
    newEscalationLevel: number;
    escalatedBy: string;
    escalatedAt: string;
}

// Event: delivery_update
export interface DeliveryUpdateAlert extends BaseAlert {
    orderId: string;
    orderNumber: string;
    status: string;
    location?: {
        latitude: number;
        longitude: number;
    };
    eta: string;
    driverName: string;
    transporterId: string;
    customerName: string;
}

// Event: low_stock_alert
export interface LowStockAlert extends BaseAlert {
    productId: string;
    productName: string;
    sku: string;
    currentStock: number;
    threshold: number;
    isOutOfStock: boolean;
    supplierId: string;
    supplierName: string;
}

// Event: supplier_status
export interface SupplierStatusAlert extends BaseAlert {
    supplierId: string;
    businessName: string;
    status: 'online' | 'offline' | 'verified' | 'suspended' | string;
    reason?: string;
    email: string;
    mobile: string;
}

// Event: transporter_status
export interface TransporterStatusAlert extends BaseAlert {
    transporterId: string;
    name: string;
    status: 'online' | 'offline' | 'verified' | 'suspended' | string;
    reason?: string;
    email: string;
    mobile: string;
    vehicleCount: number;
}

// Event: admin_notification
export interface AdminNotificationAlert extends BaseAlert {
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
    action?: {
        label: string;
        url: string;
    };
    metadata?: Record<string, unknown>;
}

// Union type for all alerts
export type WebSocketAlert =
    | NewOrderAlert
    | OrderStatusChangeAlert
    | AssignmentAlert
    | PaymentUpdateAlert
    | BreachAlert
    | BreachEscalationAlert
    | DeliveryUpdateAlert
    | LowStockAlert
    | SupplierStatusAlert
    | TransporterStatusAlert
    | AdminNotificationAlert;

// Alert for display in UI with additional metadata
export interface DisplayAlert {
    id: string;
    eventType: string;
    title: string;
    message: string;
    priority: AlertPriority;
    category: AlertCategory;
    timestamp: Date;
    read: boolean;
    dismissed: boolean;
    action?: {
        label: string;
        url: string;
    };
    rawData: WebSocketAlert;
}

// Event name mapping
export const ALERT_EVENTS = [
    'new_order',
    'order_status_change',
    'assignment_alert',
    'payment_update',
    'breach_alert',
    'breach_escalation',
    'delivery_update',
    'low_stock_alert',
    'supplier_status',
    'transporter_status',
    'admin_notification',
] as const;

export type AlertEventName = typeof ALERT_EVENTS[number];

// Category mapping for events
export const EVENT_CATEGORY_MAP: Record<AlertEventName, AlertCategory> = {
    new_order: 'orders',
    order_status_change: 'orders',
    assignment_alert: 'orders',
    payment_update: 'payments',
    breach_alert: 'breaches',
    breach_escalation: 'breaches',
    delivery_update: 'orders',
    low_stock_alert: 'stock',
    supplier_status: 'status',
    transporter_status: 'status',
    admin_notification: 'general',
};

// Navigation URL generators
export const getAlertNavigationUrl = (eventType: AlertEventName, data: WebSocketAlert): string | null => {
    switch (eventType) {
        case 'new_order':
        case 'order_status_change':
        case 'assignment_alert':
        case 'delivery_update':
            return `/orders?orderId=${(data as NewOrderAlert | OrderStatusChangeAlert | AssignmentAlert | DeliveryUpdateAlert).orderId}`;

        case 'payment_update':
            return `/earnings/payments?orderId=${(data as PaymentUpdateAlert).orderId}`;

        case 'breach_alert':
        case 'breach_escalation':
            return `/breaches?breachId=${(data as BreachAlert | BreachEscalationAlert).breachId}`;

        case 'low_stock_alert':
            return `/supplier-products?productId=${(data as LowStockAlert).productId}`;

        case 'supplier_status':
            return `/suppliers?id=${(data as SupplierStatusAlert).supplierId}`;

        case 'transporter_status':
            return `/transporters?id=${(data as TransporterStatusAlert).transporterId}`;

        case 'admin_notification':
            return (data as AdminNotificationAlert).action?.url || null;

        default:
            return null;
    }
};

// Alert title generators
export const getAlertTitle = (eventType: AlertEventName, data: WebSocketAlert): string => {
    switch (eventType) {
        case 'new_order':
            return 'New Order';
        case 'order_status_change':
            return 'Order Status Updated';
        case 'assignment_alert':
            const assignmentData = data as AssignmentAlert;
            return `${assignmentData.assignmentType === 'supplier' ? 'Supplier' : 'Transporter'} Assigned`;
        case 'payment_update':
            const paymentStatus = (data as PaymentUpdateAlert).status;
            return paymentStatus === 'failed' ? 'Payment Failed' : 'Payment Update';
        case 'breach_alert':
            return 'SLA Breach Detected';
        case 'breach_escalation':
            return 'Breach Escalated';
        case 'delivery_update':
            return 'Delivery Update';
        case 'low_stock_alert':
            return (data as LowStockAlert).isOutOfStock ? 'Out of Stock!' : 'Low Stock Alert';
        case 'supplier_status':
            return 'Supplier Status Changed';
        case 'transporter_status':
            return 'Transporter Status Changed';
        case 'admin_notification':
            return (data as AdminNotificationAlert).title;
        default:
            return 'Notification';
    }
};

// Alert message generators
export const getAlertMessage = (eventType: AlertEventName, data: WebSocketAlert): string => {
    switch (eventType) {
        case 'new_order': {
            const d = data as NewOrderAlert;
            const amount = d.totalAmount?.toLocaleString?.() ?? '0';
            return `Order #${d.orderNumber ?? 'N/A'} placed by ${d.customerName ?? 'Customer'} - ₹${amount}`;
        }
        case 'order_status_change': {
            const d = data as OrderStatusChangeAlert;
            return `Order #${d.orderNumber ?? 'N/A'}: ${d.previousStatus ?? 'unknown'} → ${d.newStatus ?? 'unknown'}`;
        }
        case 'assignment_alert': {
            const d = data as AssignmentAlert;
            return `${d.assigneeName ?? 'Assignee'} assigned to order #${d.orderNumber ?? 'N/A'}`;
        }
        case 'payment_update': {
            const d = data as PaymentUpdateAlert;
            const amount = d.amount?.toLocaleString?.() ?? '0';
            return `Payment ${d.status ?? 'updated'} for order #${d.orderNumber ?? 'N/A'} - ₹${amount}`;
        }
        case 'breach_alert': {
            const d = data as BreachAlert;
            return `${d.breachType ?? 'SLA breach'} on order #${d.orderNumber ?? 'N/A'} - ${d.severity ?? 'unknown'} severity`;
        }
        case 'breach_escalation': {
            const d = data as BreachEscalationAlert;
            return `Breach on order #${d.orderNumber ?? 'N/A'} escalated to level ${d.newEscalationLevel ?? '?'}`;
        }
        case 'delivery_update': {
            const d = data as DeliveryUpdateAlert;
            return `Order #${d.orderNumber ?? 'N/A'}: ${d.status ?? 'Update'} - Driver: ${d.driverName ?? 'Unknown'}`;
        }
        case 'low_stock_alert': {
            const d = data as LowStockAlert;
            return d.isOutOfStock
                ? `${d.productName ?? 'Product'} is out of stock!`
                : `${d.productName ?? 'Product'} has only ${d.currentStock ?? 0} units left`;
        }
        case 'supplier_status': {
            const d = data as SupplierStatusAlert;
            return `${d.businessName ?? 'Supplier'} is now ${d.status ?? 'updated'}${d.reason ? `: ${d.reason}` : ''}`;
        }
        case 'transporter_status': {
            const d = data as TransporterStatusAlert;
            return `${d.name ?? 'Transporter'} is now ${d.status ?? 'updated'}${d.reason ? `: ${d.reason}` : ''}`;
        }
        case 'admin_notification':
            return (data as AdminNotificationAlert).message ?? 'New notification';
        default:
            return 'New notification received';
    }
};
